import {
  useQuery,
  getPendingAccessRequests,
  getBmEligibleMembers,
  approveAccessByBuildingManager,
  grantBuildingServicesAccess,
  rejectAccessRequest,
  getUserMembership,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import JoinSharePanel from "./JoinSharePanel";

export default function BmAccessPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const locationId = selectedLocation?.id || "";

  const { data: membership } = useQuery(
    getUserMembership,
    { locationId },
    { enabled: !!locationId && !!user },
  );
  const mem = membership as any;
  const isManager =
    !!user?.isAdmin ||
    (mem && ["ADMIN", "MANAGER"].includes(mem.role));

  const { data: requests, refetch: refetchReq } = useQuery(
    getPendingAccessRequests,
    { locationId, scope: "building" },
    { enabled: !!locationId && isManager },
  );
  const { data: members, refetch: refetchMembers } = useQuery(
    getBmEligibleMembers,
    { locationId },
    { enabled: !!locationId && isManager },
  );

  const reqList = (requests as any[]) || [];
  const memberList = (members as any[]) || [];

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl px-4 py-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-sky-700">
          Location management
        </p>
        <h1 className="mt-1 text-3xl font-bold">Members</h1>
        <p className="mt-2 text-neutral-600">
          Share the join link and approve membership requests. One approval is
          enough: approved members can post and see members-only content.
        </p>

        {!locationId && (
          <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Select a location first.
          </p>
        )}

        {locationId && !isManager && (
          <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Only location managers can approve members and manage invite codes.
          </p>
        )}

        {locationId && isManager && (
          <div className="mt-6">
            <JoinSharePanel
              locationId={locationId}
              locationCode={selectedLocation?.locationCode}
            />
          </div>
        )}

        <section className="mt-8">
          <h2 className="text-lg font-semibold">Membership requests</h2>
          <div className="mt-3 space-y-3">
            {reqList.map((r) => (
              <article
                key={r.id}
                className="rounded-2xl border border-neutral-200 bg-white p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold">
                      {r.user?.fullName || r.user?.username || r.user?.email}
                    </p>
                    <p className="text-xs uppercase tracking-wide text-neutral-500">
                      {r.status.replace("_", " ")}
                    </p>
                    {r.unitNumber && (
                      <p className="text-sm">Unit {r.unitNumber}</p>
                    )}
                    {r.message && (
                      <p className="mt-1 text-sm text-neutral-600">{r.message}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="rounded-xl bg-sky-700 px-3 py-2 text-sm font-semibold text-white"
                      onClick={async () => {
                        await approveAccessByBuildingManager({
                          requestId: r.id,
                        });
                        await Promise.all([refetchReq(), refetchMembers()]);
                      }}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="rounded-xl border px-3 py-2 text-sm"
                      onClick={async () => {
                        await rejectAccessRequest({ requestId: r.id });
                        await refetchReq();
                      }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </article>
            ))}
            {locationId && reqList.length === 0 && (
              <p className="text-sm text-neutral-500">No open requests.</p>
            )}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-lg font-semibold">
            Members without work-order access
          </h2>
          <p className="text-sm text-neutral-500">
            Optional: let these members file building work orders.
          </p>
          <div className="mt-3 space-y-3">
            {memberList.map((m) => (
              <article
                key={m.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-neutral-200 bg-white p-4"
              >
                <div>
                  <p className="font-semibold">
                    {m.user?.fullName || m.user?.username || m.user?.email}
                  </p>
                  {m.unitNumber && (
                    <p className="text-sm text-neutral-500">Unit {m.unitNumber}</p>
                  )}
                </div>
                <button
                  type="button"
                  className="rounded-xl bg-neutral-900 px-3 py-2 text-sm font-semibold text-white"
                  onClick={async () => {
                    await grantBuildingServicesAccess({
                      userId: m.userId,
                      locationId,
                    });
                    await refetchMembers();
                  }}
                >
                  Unlock services
                </button>
              </article>
            ))}
            {locationId && memberList.length === 0 && (
              <p className="text-sm text-neutral-500">
                Everyone eligible already has service access.
              </p>
            )}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
